/**
 * SeoCopyBlock — renders SEO additional copy below the product grid.
 *
 * Ported from:
 * - hec-composable/hec/components/seo-copy-block/seoCopyBlock.tsx
 * - sfcc-website/org_hec/cartridge/templates/default/search/searchResultsNoDecorator.isml
 *   (category-seo-bottom-text section)
 *
 * The category H1 lives at the top of the PLP (above filter row); this block
 * renders only the long-form SEO marketing copy with a Read more/Read less toggle.
 */
import { useState } from "react";

import { PrefetchBuilderAnchors } from "~/hec/components/prefetch-builder-anchors";
import { rewriteSfccContentLinks } from "~/hec/utils/sfcc-content-links";

const CONTENT_CLASS =
  "text-sm leading-relaxed lg:text-base [&_a]:font-medium [&_a]:text-blue-04 [&_a]:underline [&_a:hover]:no-underline [&_h2]:my-4 [&_h2]:font-bold [&_h2]:font-serif [&_p]:mb-4 [&_p]:font-grotesque [&_p]:text-blue-03";

interface SeoCopyBlockProps {
  seoAdditionalCopy?: string;
  heading?: string;
}

export function SeoCopyBlock({
  seoAdditionalCopy,
  heading,
}: SeoCopyBlockProps) {
  if (!seoAdditionalCopy) return null;

  // Strip stale "read less" text that may be in CMS content.
  // Downgrade <h1> → <h2> to prevent duplicate H1 alongside DeferH1Seo's
  // sr-only H1 (seo.h1 = category name). BM authors may author H1 tags in
  // c_seoAdditionalCopy for formatting; rendering them would produce multiple
  // H1s on the page. (SEO-15 / SFCC-11170)
  // When a heading prop is supplied, also strip a leading <h2> from the BM
  // content — it is typically the same category name that the heading prop
  // already renders, and showing it twice produces duplicate adjacent H2s.
  // Rewrite instance-host links first so BM content-picker links that SCAPI
  // resolved to the raw sandbox host stay same-origin. (SFCC-11795.)
  let cleanedCopy = rewriteSfccContentLinks(seoAdditionalCopy)
    .replace(/read less/gi, "")
    .replace(/<h1(\s[^>]*)?>/gi, "<h2$1>")
    .replace(/<\/h1>/gi, "</h2>");

  if (heading) {
    cleanedCopy = cleanedCopy.replace(/^\s*<h2[^>]*>.*?<\/h2>\s*/is, "");
  }

  if (!cleanedCopy) return null;

  // Split at the first </p> — first paragraph always visible, everything
  // after it hidden behind "Read more". Matches prod's character-based
  // truncation which effectively shows only the first paragraph collapsed.
  const firstParaEnd = cleanedCopy.indexOf("</p>");
  const firstPart =
    firstParaEnd >= 0 ? cleanedCopy.slice(0, firstParaEnd + 4) : cleanedCopy;
  const remainder =
    firstParaEnd >= 0 ? cleanedCopy.slice(firstParaEnd + 4).trim() : "";

  return (
    <div className="mx-auto w-[90%] self-center pb-6">
      {heading && (
        <h2 className="mb-4 text-left font-serif text-xl font-bold text-blue-04">
          {heading}
        </h2>
      )}
      <ExpandableCopy
        firstPart={firstPart}
        remainder={remainder}
        expandText="Read more"
        collapseText="Read less"
      />
    </div>
  );
}

function ExpandableCopy({
  firstPart,
  remainder,
  expandText,
  collapseText,
}: {
  firstPart: string;
  remainder: string;
  expandText: string;
  collapseText: string;
}) {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <div>
      <PrefetchBuilderAnchors>
        <div
          className={CONTENT_CLASS}
          dangerouslySetInnerHTML={{
            __html: firstPart + (isExpanded ? remainder : ""),
          }}
        />
      </PrefetchBuilderAnchors>
      {remainder && (
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          className="mt-2 font-grotesque text-sm font-medium"
          data-testid="seo-copy-expand-button"
        >
          {isExpanded ? (
            <span className="underline">{collapseText}</span>
          ) : (
            <>
              ... <span className="underline">{expandText}</span>
            </>
          )}
        </button>
      )}
    </div>
  );
}
